/**
 * The checkout tab's side of the Speedy Flow's popup protocol.
 *
 * The flow opens Stripe in a tab and keeps the originating page alive behind the modal. Stripe
 * sends the tab back to `/checkout-completed` or `/checkout-cancelled`, and from there the tab's
 * only job is to tell its opener and get out of the way.
 *
 * Crucially the message is *not* proof of payment — it races the `checkout.session.completed`
 * webhook that actually grants the entitlement. It only means "go and look now" (ADR 0003).
 */

/** Marks a return path as belonging to a popup rather than a normal full-page checkout. */
export const POPUP_PARAM = 'zlpopup';

export const PAID_MESSAGE = 'zl-checkout-paid';
export const SSO_SUCCESS_MESSAGE = 'zl-sso-success';
export const SSO_FAILURE_MESSAGE = 'zl-sso-failure';
export const CANCELLED_MESSAGE = 'zl-checkout-cancelled';

/**
 * Carries the outcome across an origin hop, when the popup's opener is on the educators app and
 * this tab has to go there before it can be heard (see `shared/educatorsApp`).
 *
 * The receiving page is a relay and nothing more: it reads this, posts exactly that message, and
 * closes. It deliberately re-derives nothing — the tab that spoke to Stripe is the one that knows
 * how it went.
 */
export const REPORT_PARAM = 'zlreport';

const REPORTABLE = new Set([PAID_MESSAGE, CANCELLED_MESSAGE, SSO_SUCCESS_MESSAGE, SSO_FAILURE_MESSAGE]);

/**
 * Read the outcome a hop is carrying, if it is one we recognise.
 *
 * Whitelisted rather than passed through, because this arrives on a URL and ends in a `postMessage`
 * to an opener: without it, a crafted link would let anyone make the page say anything to whatever
 * opened it. The openers only act on messages they know, so an unrecognised one is already inert —
 * this makes it inexpressible as well.
 */
export const readReport = (search: string): string | undefined => {
  const type = new URLSearchParams(search).get(REPORT_PARAM);
  return type && REPORTABLE.has(type) ? type : undefined;
};

/** Attach an outcome to a hop URL. */
export const withReport = (url: string, type: string) => {
  const parsed = new URL(url);
  parsed.searchParams.set(REPORT_PARAM, type);
  return parsed.toString();
};

/**
 * How long the tab lingers before closing, to give the webhook a head start.
 *
 * Not correctness — the opener polls until the entitlement appears regardless — but it turns the
 * common case into a single successful check rather than a check, a miss, and a wait.
 */
export const WEBHOOK_HEAD_START_MS = 2500;

/** Add the popup marker to a return path, preserving any query string it already has. */
export const withPopupMarker = (path: string) => {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  params.set(POPUP_PARAM, '1');
  return `${pathname}?${params.toString()}`;
};

/** Whether this page load is a checkout tab returning to its opener. */
export const isPopupReturn = (search: string) => {
  const redirect = new URLSearchParams(search).get('co-redirect');
  if (!redirect) {
    return false;
  }
  const [, redirectSearch = ''] = decodeURIComponent(redirect).split('?');
  return new URLSearchParams(redirectSearch).get(POPUP_PARAM) === '1';
};

/**
 * Tell the opener what happened, then close.
 *
 * `onNoOpener` is what this tab would have done if it had never been a popup, and it is not
 * optional in practice. A tab can carry the popup marker and still have no opener: when the browser
 * blocks the popup the flow falls back to sending *this* tab to Stripe, and the return path it
 * built earlier still says `zlpopup=1`. There is nobody to post to, and `window.close()` does
 * nothing to a tab a script did not open — so without a fallback the visitor sits on the loader for
 * good, having just paid.
 */
export const reportToOpenerAndClose = (type: string, delayMs = 0, onNoOpener?: () => void) => {
  const finish = () => {
    // `closed` is readable cross-origin, so this holds for an educators opener too.
    const { opener } = window as { opener: Window | null };
    if (!opener || opener.closed) {
      onNoOpener?.();
      return;
    }
    try {
      opener.postMessage({ type }, window.location.origin);
    } catch (err) {
      console.error('Could not notify the opener', err);
    }
    window.close();
  };
  if (delayMs > 0) {
    setTimeout(finish, delayMs);
  } else {
    finish();
  }
};
